import {
  Profile,
  ProfileData,
  ContactPhone,
  ContactEmail,
  BulkCreateContactAddressesRequest,
  CreateContactAddressRequest,
  ContactDetailResponse,
  ProfileInfo,
  UpdateContactRequest,
  SuffixChoice,
} from '../types';
import { AddressNew } from '@erm/cluster';
import {
  getPrimaryAddress,
  getPrimaryEmail,
  getPrimaryPhones,
} from '../constants';

export function profileDetailsMapper(
  data: ContactDetailResponse,
): Omit<ProfileInfo['personalDetails'], 'email' | 'isPi' | 'timeZone'> {
  return {
    id: data.id,
    firstName: data.first_name,
    middleName: data.middle_name,
    lastName: data.last_name,
    legacyId: data.legacy_id,
    suffix: data.suffix,
    pronouns: data.pronouns,
    npi: data.npi,
    prefix: data.prefix,
  };
}

export function profileInfoMapper(profile: Profile): ProfileData {
  return {
    user_id: profile.user_id,
    contact_id: profile.contact_id,
    contact_version: profile.contact_version,
    organizationId: profile.organization_id,
    info: {
      personalDetails: {
        id: profile.contact_id,
        firstName: profile.first_name,
        middleName: profile.middle_name,
        lastName: profile.last_name,
        suffix: profile.suffix,
        prefix: profile.prefix,
        legacyId: profile.legacy_id,
        pronouns: profile.pronouns,
        timeZone: profile.timezone,
        npi: profile.npi,
        isPi: profile.is_pi,
        email: profile.email,
      },
      role: {
        role: {
          id: profile.role.id,
          label: profile.role.is_internal
            ? profile.role.description
            : profile.role.name,
        },
        qcFunction: { id: profile.attribute, label: profile.attribute },
        isInternal: profile.role.is_internal,
      },
      status: profile.status,
      systemIds: {
        userId: profile.user_id,
        primaryContactId: `${profile.legacy_id ?? ''}`,
        additionalContactIds: profile.additional_legacy_ids,
        oktaId: profile.email,
        investigatorWcgId: profile.wcg_id,
      },
    },
    addresses: getPrimaryAddress(profile.addresses),
    degrees: profile.degrees,
    specialties: profile.specialty,
    phones: getPrimaryPhones(profile.phones),
    emails: getPrimaryEmail(profile.emails),
  };
}

export function mapUpdateContactRequest(
  info: ProfileInfo['personalDetails'],
  additionalLegacyId = '',
  primaryContactId: number | null = null,
): UpdateContactRequest {
  return {
    first_name: info.firstName,
    middle_name: info.middleName,
    last_name: info.lastName,
    suffix: info.suffix as SuffixChoice,
    pronouns: info.pronouns,
    npi: info.npi,
    legacy_id: primaryContactId,
    additional_legacy_ids:
      additionalLegacyId?.length > 0
        ? additionalLegacyId
            ?.split(',')
            .map((legacyId) => parseInt(legacyId, 10))
        : [],
  };
}

const isPhoneContact = (
  contact: ContactPhone | ContactEmail,
): contact is ContactPhone => 'phone' in contact;

export function isPhoneContacts(
  contact: ContactPhone[] | ContactEmail[] | undefined,
): contact is ContactPhone[] {
  return Array.isArray(contact) && contact.every((c) => isPhoneContact(c));
}

export function mapAddressPayload(
  addresses: AddressNew[],
): BulkCreateContactAddressesRequest {
  if (!addresses?.length) {
    return {} as BulkCreateContactAddressesRequest;
  }
  const _addresses: CreateContactAddressRequest[] = [];
  for (const address of addresses) {
    const mappedAddress: CreateContactAddressRequest = {
      address1: address.address1,
      address2: address.address2,
      address3: address.address3,
      city: address.city,
      state: address.state,
      zip_code: address.zipCode,
      country_id: address.country.id,
      is_primary: address.isPrimary,
    };

    mappedAddress.editted = address.editted ?? false;

    _addresses.push(mappedAddress);
  }
  return {
    addresses: _addresses,
  };
}

export function getErrorDisplayMessage(
  key: 'add' | 'edit' | 'delete',
  type: 'phone' | 'email',
) {
  if (type === 'phone') {
    if (key === 'add') {
      return 'At least one primary phone number is required. Please set a phone number as primary before saving this contact.';
    }
    if (key === 'edit') {
      return 'At least one primary phone number is required. Please set another phone number as primary before saving this contact.';
    }
    if (key === 'delete') {
      return 'At least one primary phone number is required. Please set another phone number as primary before deleting this contact.';
    }
  }
  return 'At least one primary email is required. Please set another email as primary before deleting this contact.';
}
